import holiday_jp from "@holiday-jp/holiday_jp"

/**
 * ドック予約の運用ルール（検査時間・3階更衣室・所要時間の目安・祝日）
 */

// 検査の開始・終了時刻（土曜も平日と同じ）
export const DOCK_EXAM_START = "08:30"
export const DOCK_EXAM_END = "18:00"
// 受付から最初の検査までの準備時間(分)
export const RECEPTION_PREP_MINUTES = 20
// 検査と検査の間の待ち時間の上限(分)。1回ごとに見る（PET-CT の IV 後の待機・ES下部の前処置など、決まった待機の分は数えない）
export const MAX_WAIT_MINUTES = 30
// 3階更衣室（8部屋）を使うコース。同時に使えるのは8名まで、全検査終了の15分後に次の人が使える
export const LOCKER_COURSES = ["CMC会員コース", "CMC後日検査"]
export const LOCKER_CAPACITY = 8
export const LOCKER_TURNOVER_MINUTES = 15
// ES下部（大腸内視鏡）の前処置（下剤）の時間(分)。ES の前にこの時間をあける
export const ES_LOWER_PREP_MINUTES = 120
// ES下部は1日この件数まで（その日の予約全体で数える）
export const ES_LOWER_DAILY_LIMIT = 4
// 受付から全検査終了までの理想の所要時間(分)
export const DOCK_IDEAL_DURATION_MINUTES = 6 * 60
// PET-CT の IV（注射）を行う時刻。IV はこの時刻にしか行わない（from の日から適用。新しい順）
export const PET_IV_SLOTS: { from: string, times: string[] }[] = [
  { from: '2027-01-01', times: ['09:00', '09:20', '09:40', '10:00', '10:20', '10:40', '11:30', '11:50', '12:10', '12:30', '13:40', '14:00', '14:20', '14:40', '15:00', '15:20'] },
  { from: '2000-01-01', times: ['09:00', '09:20', '09:40', '10:00', '10:20', '10:40', '11:30', '11:50', '12:10', '12:30', '12:50', '13:10', '14:20', '14:40', '15:00', '15:20'] },
]
// IV 開始から PET-CT 撮影開始までの時間(分)。頭部は40分（IV終了の30分後）、それ以外は70分（IV終了の60分後）
// 仮予約・予約確定時の IV 時刻（contexts/enviroments.ts の getLatestIvTime）と同じ
export const PET_IV_LEAD_MINUTES = 70
export const PET_HEAD_IV_LEAD_MINUTES = 40

// 診療予約（問診・子宮細胞診・HPV）。再計算では並べず、予約確定時に受付からの固定の枠で登録する
export const CONSULTING_ORDER = ['QST', 'UCT', 'HPV']
// 婦人科の検査（US(L)・子宮細胞診・HPV）。コースに1つでもあれば、火・水・金の 9:15〜12:30 に行う
export const GYNECOLOGY_MODALITIES = ['US(L)', 'UCT', 'HPV']
export const GYNECOLOGY_WEEKDAYS = [2, 3, 5]
export const GYNECOLOGY_START = '09:15'
export const GYNECOLOGY_END = '12:30'

const dateKey = (date: Date) => `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}-${`0${date.getDate()}`.slice(-2)}`

/**
 * その日の PET-CT の IV の時刻
 */
export const getPetIvSlots = (date: Date) => PET_IV_SLOTS.find(v => v.from <= dateKey(date))!.times

// IV を行う PET-CT か（CT だけの撮影は IV なし。contexts/enviroments.ts の isPet と同じ）
export const isPetScan = (modalityID: string, body1: string, body2: string) => modalityID.startsWith('PET-CT') && !body1.startsWith('CT') && !body2.startsWith('CT')
export const getPetIvLeadMinutes = (body1: string, body2: string) => body1.startsWith('頭') || body2 === '頭部' ? PET_HEAD_IV_LEAD_MINUTES : PET_IV_LEAD_MINUTES

export const atTime = (date: Date, hhmm: string) => {
  const result = new Date(date)
  const [hours, minutes] = hhmm.split(":").map(Number)
  result.setHours(hours, minutes, 0, 0)
  return result
}

const formatTime = (date: Date) => `${date.getHours()}:${`0${date.getMinutes()}`.slice(-2)}`

/**
 * 診療予約（問診・子宮細胞診・HPV）の時刻。予約確定時（ドック予約画面の saveDock）はこの時刻で登録する
 * 1件なら受付10〜20分後、2件なら受付10分後から5分ずつ、3件なら受付10分後から10分ずつ
 * 子宮細胞診・HPV（婦人科）が 9:15 より前になる場合は、間隔を保ったまま 9:15 からにずらす（問診はずらさない）
 */
export const getConsultingSlot = (contents: KV[], modality: string, reception: Date) => {
  const present = CONSULTING_ORDER.filter(m => contents.some(v => v["modality"] === m))
  const slotOf = (index: number) => {
    const minutes = (n: number) => new Date(reception.getTime() + n * 60000)
    if (present.length === 1) return { start: minutes(10), end: minutes(20) }
    if (present.length === 2) return { start: minutes(10 + index * 5), end: minutes(15 + index * 5) }
    return { start: minutes(10 + index * 10), end: minutes(20 + index * 10) }
  }
  const slot = slotOf(present.indexOf(modality))
  if (!GYNECOLOGY_MODALITIES.includes(modality)) return slot
  const firstGynecology = Math.min(...present.map((m, i) => GYNECOLOGY_MODALITIES.includes(m) ? slotOf(i).start.getTime() : Infinity))
  const shift = Math.max(0, atTime(reception, GYNECOLOGY_START).getTime() - firstGynecology)
  return { start: new Date(slot.start.getTime() + shift), end: new Date(slot.end.getTime() + shift) }
}

const GYNECOLOGY_LABELS: Record<string, string> = { 'US(L)': 'US(L)', UCT: '子宮細胞診', HPV: 'HPV' }

/**
 * 婦人科の検査（US(L)・子宮細胞診・HPV）が、火・水・金の 9:15〜12:30 に収まるか
 * US(L) は再計算で決めた時刻、子宮細胞診・HPV は予約確定時の固定の枠で判定する
 * @param contents コース内容（問診・子宮細胞診・HPV を含むもの）
 * @param duration calcDockSchedule の duration
 * @returns 収まらない場合、その理由
 */
export const checkGynecology = (contents: KV[], reception: Date, duration: KV) => {
  const exams = contents.filter(v => GYNECOLOGY_MODALITIES.includes(v["modality"]))
  if (!exams.length) return undefined
  const rule = `婦人科の検査（US(L)・子宮細胞診・HPV）は火・水・金の${GYNECOLOGY_START.replace(/^0/, '')}〜${GYNECOLOGY_END}のみ`
  if (!GYNECOLOGY_WEEKDAYS.includes(reception.getDay())) return `${rule}（${'日月火水木金土'[reception.getDay()]}曜日）`
  const first = atTime(reception, GYNECOLOGY_START)
  const last = atTime(reception, GYNECOLOGY_END)
  for (const v of exams) {
    const time = v["modality"] === 'US(L)'
      ? duration[['US(L)', v["modality_body1"], v["modality_body2"], v["modality_contrast"]].map(w => w ?? '').join('')]
      : getConsultingSlot(contents, v["modality"], reception)
    if (!time) continue
    const [start, end] = [new Date(time.start), new Date(time.end)]
    if (start < first || end > last) return `${rule}（${GYNECOLOGY_LABELS[v["modality"]]} が ${formatTime(start)}〜${formatTime(end)}）`
  }
  return undefined
}

/**
 * 受付から全検査終了までの時間帯（スケジュール管理する検査の最後の終了時刻）
 * @param duration calcDockSchedule の duration
 */
export const getDockStay = (reception: Date, duration: KV) => {
  const ends = Object.values(duration).filter((v: KV) => !v.no_schedule).map((v: KV) => new Date(v.end).getTime())
  // スケジュール管理しない検査だけの場合は、登録時と同じく受付20分後
  const end = new Date(ends.length ? Math.max(...ends) : reception.getTime() + 20 * 60000)
  return { start: new Date(reception), end, minutes: (end.getTime() - reception.getTime()) / 60000 }
}

export const isLockerCourse = (courseName?: string) => !!courseName && LOCKER_COURSES.some(v => courseName.includes(v))

/**
 * 3階更衣室の空きを確認する
 * 対象コースの受診者が、受付から全検査終了の15分後まで1部屋を使うとして、同時に使う人数が8名を超えないか
 * @param stay 受付から全検査終了まで
 * @param events その日の予約イベント（他の受診者の更衣室の利用時間を、ドックごとの受付時刻と最後の検査の終了時刻から求める）
 * @param dockID 自分自身のドック予約番号（更新時に自分を数えないため）
 * @returns 満室なら、その時間帯に更衣室を使っている人数
 */
export const checkLockerFull = (courseName: string | undefined, stay: { start: Date, end: Date }, events: KV[], dockID?: string | number) => {
  if (!isLockerCourse(courseName)) return undefined
  const turnover = LOCKER_TURNOVER_MINUTES * 60000
  const others: Record<string, { start: number, end: number }> = {}
  for (const event of events) {
    const props = event.extendedProps ?? {}
    if (!props.dockID || String(props.dockID) === String(dockID ?? '') || !isLockerCourse(props.courseName)) continue
    const end = new Date(event.end).getTime() + turnover
    const reception = props.checkin && /^\d{1,2}:\d{2}$/.test(props.checkin) ? atTime(new Date(event.start), props.checkin).getTime() : new Date(event.start).getTime()
    const stayOfOther = others[props.dockID] ||= { start: reception, end }
    stayOfOther.start = Math.min(stayOfOther.start, reception)
    stayOfOther.end = Math.max(stayOfOther.end, end)
  }
  const start = stay.start.getTime()
  const end = stay.end.getTime() + turnover
  const intervals = Object.values(others)
  // 自分の利用時間帯の中で、他の受診者が同時に使っている最大人数
  const points = [start, ...intervals.map(v => v.start).filter(t => t > start && t < end)]
  const maxInUse = Math.max(...points.map(t => intervals.filter(v => v.start <= t && t < v.end).length))
  return maxInUse >= LOCKER_CAPACITY ? maxInUse : undefined
}

/**
 * 日本の祝日（振替休日・国民の休日を含む）の名前。祝日でなければ undefined
 * 祝日データは @holiday-jp/holiday_jp による。法改正などで祝日が変わった場合はパッケージの更新が必要
 */
export const getHolidayName = (date: Date) => {
  return (holiday_jp.holidays as Record<string, { name: string }>)[dateKey(date)]?.name
}

const isEsLower = (modality?: string, body2?: string) => modality === 'ES' && body2 === '下部'

/**
 * ES下部の1日の上限を超えるか
 * @param contents コース内容（ES下部を含まなければ判定しない）
 * @param events その日の予約イベント
 * @param dockID 自分自身のドック予約番号（更新時に自分を数えないため）
 * @returns 上限に達している場合、その日のES下部の件数
 */
export const checkEsLowerFull = (contents: KV[], events: KV[], dockID?: string | number) => {
  if (!contents.some(v => isEsLower(v["modality"], v["modality_body2"] ?? undefined))) return undefined
  const count = events.filter(v => {
    const props = v.extendedProps ?? {}
    if (props.dockID !== undefined && String(props.dockID) === String(dockID ?? '')) return false
    return isEsLower(props.modalityID ?? v.resourceId, props.body2)
  }).length
  return count >= ES_LOWER_DAILY_LIMIT ? count : undefined
}
